import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import moment from 'moment';
import { map, filter, capitalize } from 'lodash';
import { decamelize } from 'humps';

import AvatarIcon from 'components/AvatarIcon';
import { IconAvatarLumaBot } from 'icons';
import { journeyEventTypes } from 'ui-utils';

const styles = (theme) => ({
  row: {
    display: 'flex',
    alignItems: 'center',
  },
  card: {
    width: '100%',
    padding: theme.spacing.unit,
    color: theme.custom.palette.colorVeryDarkGrayishBlue,
  },
  cardHeader: {
    paddingBottom: 0,
    '& span': {
      fontWeight: 600,
      color: theme.custom.palette.colorVeryDarkGrayishBlue,
    },
  },
  icon: {
    width: theme.spacing.unit * 3,
    height: theme.spacing.unit * 3,
    marginTop: theme.spacing.unit / 2,
    marginBottom: theme.spacing.unit / 2,
    marginRight: theme.spacing.unit,
    '& span': {
      fontSize: theme.spacing.unit * 1.5 - 2,
      color: 'white',
    },
  },
});

class PatientJourneyCardYear extends Component {
  render() {
    const {
      classes,
      onClick,
      event: { start },
    } = this.props;

    return (
      <Card>
        <CardActionArea className={classes.card} onClick={onClick}>
          <CardHeader
            className={classnames(classes.cardHeader, classes.cardHeaderYear)}
            title={moment(start)
              .utc()
              .year()}
          />
          <CardContent>
            {/*
              Using journeyEventTypes to order the events properly
              https://app.clickup.com/1227173/1273459/t/a01qp
             */}
            <Grid container>{map(journeyEventTypes, this.renderType)}</Grid>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  }

  renderType = (type) => {
    const {
      event: { data },
    } = this.props;
    if (!data || !data[type]) {
      return;
    }
    return this.renderItem(data[type], type);
  };

  handleFormsNames = (value) => {
    // Handle LumaBot flows
    const numberOfLumaBotForms = filter(value, (form) => form.lumabot).length;
    const lumaBotForms = numberOfLumaBotForms === 1 ? '1 LumaBot flow' : `${numberOfLumaBotForms} LumaBot flows`;

    // Handle Intake forms
    const numberOfIntakeForms = filter(value, (form) => form.patientFormTemplateType === 'intake' && !form.lumabot)
      .length;
    const intakeForms = numberOfIntakeForms === 1 ? '1 Intake form' : `${numberOfIntakeForms} Intake forms`;

    // Handle CCDA forms
    const numberOfCcdaForms = filter(value, (form) => form.patientFormTemplateType === 'ccda').length;
    const ccdaForms = numberOfCcdaForms === 1 ? '1 CCDA form' : `${numberOfCcdaForms} CCDA forms`;

    // Handle Feedback forms
    const numberOfFeedbackForms = filter(value, (form) => form.patientFormTemplateType === 'feedback').length;
    const feedbackForms = numberOfFeedbackForms === 1 ? '1 Feedback form' : `${numberOfFeedbackForms} Feedback forms`;

    // Handle PS forms
    const numberOfpreQualificationForms = filter(value, (form) => form.patientFormTemplateType === 'pre-qualification')
      .length;
    const preQualificationForms =
      numberOfpreQualificationForms === 1
        ? '1 Pre-scheduling form'
        : `${numberOfpreQualificationForms} Pre-scheduling forms`;

    return {
      intakeForms: numberOfIntakeForms > 0 ? intakeForms : null,
      lumaBotForms: numberOfLumaBotForms > 0 ? lumaBotForms : null,
      ccdaForms: numberOfCcdaForms > 0 ? ccdaForms : null,
      feedbackForms: numberOfFeedbackForms > 0 ? feedbackForms : null,
      preQualificationForms: numberOfpreQualificationForms > 0 ? preQualificationForms : null,
    };
  };

  renderItem = (value, key) => {
    const { classes } = this.props;
    const num = value.length;
    let name;
    switch (true) {
      case /patientForms/i.test(key): {
        const { intakeForms, lumaBotForms, ccdaForms, feedbackForms, preQualificationForms } = this.handleFormsNames(
          value,
        );
        return (
          <Grid key={key} xs={12} item container>
            {!!intakeForms && (
              <Grid xs={12} item className={classes.row}>
                <AvatarIcon className={classes.icon}>{key}</AvatarIcon>
                {intakeForms}
              </Grid>
            )}
            {!!lumaBotForms && (
              <Grid xs={12} item className={classes.row}>
                <IconAvatarLumaBot className={classes.icon}>{key}</IconAvatarLumaBot>
                {lumaBotForms}
              </Grid>
            )}
            {!!ccdaForms && (
              <Grid xs={12} item className={classes.row}>
                <AvatarIcon className={classes.icon}>{key}</AvatarIcon>
                {ccdaForms}
              </Grid>
            )}
            {!!feedbackForms && (
              <Grid xs={12} item className={classes.row}>
                <AvatarIcon className={classes.icon}>{key}</AvatarIcon>
                {feedbackForms}
              </Grid>
            )}
            {!!preQualificationForms && (
              <Grid xs={12} item className={classes.row}>
                <AvatarIcon className={classes.icon}>{key}</AvatarIcon>
                {preQualificationForms}
              </Grid>
            )}
          </Grid>
        );
      }
      case /insurance/i.test(key):
        name = num === 1 ? 'Insurance upload' : 'Insurance uploads';
        break;
      case /diagnos/i.test(key):
        name = 'Diagnosis';
        break;
      case /feedback/i.test(key):
        name = num === 1 ? 'Feedback recorded' : 'Feedbacks recorded';
        break;
      default:
        name = capitalize(decamelize(key, { separator: ' ' }));
        if (num === 1) {
          name = name.slice(0, -1);
        }
        break;
    }
    return (
      <Grid key={key} xs={12} item className={classes.row}>
        <AvatarIcon className={classes.icon}>{key}</AvatarIcon>
        {`${num} ${name}`}
      </Grid>
    );
  };
}

PatientJourneyCardYear.propTypes = {
  classes: PropTypes.object.isRequired,
  event: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
};

export default withStyles(styles)(PatientJourneyCardYear);
